import * as React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/common.utils";
import { Button } from "@/components/ui/button";

interface SearchFieldProps extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "className"> {
  value?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onClear?: () => void;
  placeholder?: string;
  shortcut?: string;
  className?: string;
  inputClassName?: string;
  testId?: string;
}

/** Forwards its ref to the input itself, so a caller that owns focus — the
 *  header's ⌘K shortcut, for one — can reach it. */
const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { value, onChange, onClear, placeholder = "Search…", shortcut, className, inputClassName, testId, ...props },
  ref,
) {
  const controlledProps = value === undefined ? {} : { value };
  return (
    <div className={cn("relative min-w-0", className)}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-dash-faint" aria-hidden="true" />
      <Input
        ref={ref}
        type="search"
        {...controlledProps}
        onChange={onChange}
        placeholder={placeholder}
        className={cn("h-11 rounded-xl border-dash-border bg-white pl-10 shadow-none", (shortcut || (value && onClear)) && "pr-12", inputClassName)}
        data-testid={testId}
        {...props}
      />
      {value && onClear ? (
        <Button variant="seamless" type="button" onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-dash-faint hover:text-dash-ink" aria-label="Clear search">
          <X className="size-4" />
        </Button>
      ) : shortcut ? (
        <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md border border-dash-border bg-white px-1.5 py-0.5 text-[10px] text-dash-placeholder sm:inline-block">{shortcut}</kbd>
      ) : null}
    </div>
  );
});

export default SearchField;
